import type {
  HeadCasingSettings,
  HeadCasingStone,
  HeadCasingTrim,
  HeadCasingJackArch,
  HeadCasingFullArch,
  HeadCasingArchedSlices,
  HeadCasingEyebrowTrim,
} from "building-types/settings/headCasingSettings";
import type { RoofSettings } from "building-types/settings/roofSettings";
import { HeadCasingType } from "building-types/settings/settingsTypesEnum";

export const roofSettingsKeys: {
  [key in keyof Omit<RoofSettings, "type" | "id">]: boolean;
} = {
  name: true,
  weldMode: true,
  peakDirection: true,
  boundaryType: true,
  roofType: true,
  startTruncationType: true,
  startTruncationHeightFactor: true,
  startTruncationNormalizedHeight: true,
  startTruncationSlopeType: true,
  startTruncationSlope: true,
  endTruncationType: true,
  endTruncationHeightFactor: true,
  endTruncationNormalizedHeight: true,
  endTruncationSlopeType: true,
  endTruncationSlope: true,
  structureType: true,
  vaultedRoofThickness: true,
  vaultedRoofRightThickness: true,
  slope: true,
  slopes: true,
  rightSlope: true,
  rightSlopes: true,
  eaveFasciaSize: true,
  rakeFasciaSize: true,
  soffitOffset: true,
  includeFasciaTopTrim: true,
  includeEaveReturnFasciaTopTrim: true,
  fasciaTopTrimSize: true,
  eaveOverhang: true,
  rakeOverhang: true,
  overhangSideOffset: true,
  overhangRearOffset: true,
  overhangDatum: true,
  eaveType: true,
  eaveCutType: true,
  addRafterTailExtension: true,
  rafterTailExtensionEdgeOffset: true,
  rafterTailExtension: true,
  rafterTailDepth: true,
  rafterTailClosed: true,
  lookoutRafters: true,
  flyRafters: true,
  flyRafterSize: true,
  fullReturnOrientation: true,
  miniHipOrientation: true,
  eaveReturnRoofSlope: true,
  eaveReturnRoofMaterial: true,
  eaveReturnDistance: true,
  eaveReturnOverhang: true,
  eaveExtensionAtCoplanarRake: true,
  eaveExtensionAtNonCoplanarRake: true,
  eaveExtensionAtCoplanarWall: true,
  rakeExtensionAtCoplanarWall: true,
  eaveFasciaHeightDatum: true,
  eaveFasciaHeightOffset: true,
  eaveLeftFasciaHeightOffsetOverride: true,
  eaveRightFasciaHeightOffsetOverride: true,
  friezeBoardOrientation: true,
  includeFriezeBoardOnPorches: true,
  rakeFriezeBoard: true,
  rakeFriezeBoardMaterialLegacy: true,
  rakeFriezeBoardExtents: true,
  eaveFriezeBoard: true,
  eaveFriezeBoardMaterialLegacy: true,
  eaveFriezeBoardExtents: true,
  wrapFriezeBoardMode: true,
  rakeBracketPropId: true,
  rakeBracketAtPeak: true,
  rakeBracketParams: true,
  rakeBracketParamsNormalized: true,
  rakeBracketTruncationParams: true,
  roofingMaterial: true,
  roofingMaterialThickness: true,
  roofingMaterialExtension: true,
  roofRidgeRoofingCapMaterial: true,
  roofRidgeRoofingCapWidth: true,
  roofRidgeRoofingCapThickness: true,
  includeRidgeVent: true,
  ridgeVentMinimumThreshold: true,
  ridgeVentEndsOffset: true,
  includeTriangleGableVent: true,
  triangleGableVentBaseWidth: true,
  gableBellyBandBoard: true,
  gableBellyBandBoardMaterial: true,
  gableBellyBandBoardAnchor: true,
  gableBellyBandBoardJustification: true,
  gableBellyBandBoardOffset: true,
  useNewRoofTrimGeneration: true,
  soffitMaterial: true,
  rakeFasciaMaterial: true,
  eaveFasciaMaterial: true,
  fasciaTopTrimMaterial: true,
  eaveBracketPlacement: true,
  eaveBracketSide: true,
  eaveBracketParamsNormalized: true,
  eaveBracketParams: true,
  eaveBracketPropId: true,

  // deprecated
  shedOverhang: true,
};

export function isHeadCasingStone(settings: HeadCasingSettings): settings is HeadCasingStone {
  return settings.headCasingType === HeadCasingType.STONE;
}

export function isHeadCasingTrim(settings: HeadCasingSettings): settings is HeadCasingTrim {
  return settings.headCasingType === HeadCasingType.TRIM;
}

export function isHeadCasingJackArch(settings: HeadCasingSettings): settings is HeadCasingJackArch {
  return settings.headCasingType === HeadCasingType.JACK_ARCH;
}

export function isHeadCasingFullArch(settings: HeadCasingSettings): settings is HeadCasingFullArch {
  return settings.headCasingType === HeadCasingType.FULL_ARCH;
}

export function isHeadCasingArchedSlices(settings: HeadCasingSettings): settings is HeadCasingArchedSlices {
  return settings.headCasingType === HeadCasingType.ARCHED_SLICES;
}

export function isHeadCasingEyebrowTrim(settings: HeadCasingSettings): settings is HeadCasingEyebrowTrim {
  return settings.headCasingType === HeadCasingType.EYEBROW_TRIM;
}
